import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, BookOpen } from 'lucide-react';
import { GabryxFooter } from '../components/GabryxBranding';
import { PilotSlotsBadge } from '../components/PilotSlotsBadge';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img 
              src="/chantix-logo.png" 
              alt="CHANTIX" 
              className="h-20 w-auto object-contain mb-4"
              data-testid="login-logo"
            />
            <p className="text-[#4B5563]">Connectez-vous à votre espace de gestion de chantier</p>
          </div>

          {location.state?.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {location.state.error}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                data-testid="login-email-input"
                className="mt-1"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <Link to="/mot-de-passe-oublie"
                  className="text-xs text-[#0055FF] hover:underline"
                  data-testid="forgot-password-link">
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#111827]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#0055FF] hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-[#E5E7EB]"></div>
            <span className="px-4 text-sm text-[#4B5563]">ou</span>
            <div className="flex-1 border-t border-[#E5E7EB]"></div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
            className="w-full border-2 border-[#111827] text-[#111827] hover:bg-gray-100"
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </Button>

          <p className="mt-6 text-center text-sm text-[#4B5563]">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-[#0055FF] font-medium hover:underline" data-testid="register-link">
              Créer un compte
            </Link>
          </p>

          {/* Badge Phase Pilote — urgence + conversion */}
          <PilotSlotsBadge className="mt-5" />

          {/* Lead Magnets — Referentiels CSPS + OPC : visibles mais reserves apres inscription */}
          <div className="mt-6 grid gap-3">
            <Link
              to="/register"
              state={{ from_referentiel: 'csps' }}
              data-testid="lead-magnet-referentiel-csps"
              className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-[#FF6B00]/40 bg-gradient-to-r from-[#FFF4EB] to-white hover:border-[#FF6B00] hover:shadow-md transition-all group"
            >
              <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#FF6B00] flex items-center justify-center text-white group-hover:scale-105 transition-transform">
                <BookOpen size={22} strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#111827] leading-tight">Référentiel CSPS · 39 pages</p>
                <p className="text-xs text-[#4B5563] mt-0.5">Guide complet Coordonnateur Sécurité : réglementation, missions, bonnes pratiques</p>
              </div>
              <span className="flex-shrink-0 text-xs font-semibold text-[#FF6B00] group-hover:underline whitespace-nowrap">S'inscrire →</span>
            </Link>

            <Link
              to="/register"
              state={{ from_referentiel: 'opc' }}
              data-testid="lead-magnet-referentiel-opc"
              className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-[#0055FF]/40 bg-gradient-to-r from-[#EFF6FF] to-white hover:border-[#0055FF] hover:shadow-md transition-all group"
            >
              <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#0055FF] flex items-center justify-center text-white group-hover:scale-105 transition-transform">
                <BookOpen size={22} strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#111827] leading-tight">Référentiel OPC · 37 pages</p>
                <p className="text-xs text-[#4B5563] mt-0.5">Guide complet Ordonnanceur Pilote : planning, pilotage, coordination, comptes rendus</p>
              </div>
              <span className="flex-shrink-0 text-xs font-semibold text-[#0055FF] group-hover:underline whitespace-nowrap">S'inscrire →</span>
            </Link>
          </div>

          <GabryxFooter variant="login" />
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1771153511650-095180992e08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnQlMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzUxNjEzMjV8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-[#0055FF]/80 flex items-center justify-center">
          <div className="text-center text-white p-8 max-w-md">
            <h2 className="text-4xl font-bold mb-4">Gérez vos chantiers efficacement</h2>
            <p className="text-lg opacity-90">
              Suivi des réserves, rapports automatisés, sécurité CSPS - tout en un seul outil mobile.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
